import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';

/**
 * V3 Pokrivenost zlata (mail 861, Vedran 12.10.2026): "kad se pojavi novo skladiste
 * treba iskociti popup u kojem se definira gdje ide, i u postavke moze ici da se to
 * po skladistima mijenja". Uloga (modul 40) i kanta odjeljka (admin 128) po skladistu.
 */

type Locale = 'hr' | 'en';

type Wh = { code: string; name: string; ownership_type: string | null; coverage_role: string; info_bucket: string | null };
type Unknown = { code: string; name: string; rows: number };
type Payload = { warehouses: Wh[]; unknown: Unknown[]; roles: string[]; buckets: string[]; can_edit: boolean };

const ROLE_LABEL: Record<string, [string, string]> = {
    poslovnica: ['Poslovnica', 'Branch'],
    veleprodaja: ['Veleprodaja (S1)', 'Wholesale (S1)'],
    akcijsko: ['Akcijsko (211)', 'Promo (211)'],
    samo_isporuke: ['Samo isporuke', 'Deliveries only'],
    nadopuna_lagera: ['Nadopuna lagera (L3)', 'Lager replenishment (L3)'],
};

const BUCKET_LABEL: Record<string, [string, string]> = {
    '': ['bez odjeljka', 'no section'],
    komisija: ['Komisija', 'Consignment'],
    slovenija: ['GG SLO', 'GG SLO'],
    bugarska: ['GTM / Bugarska', 'GTM / Bulgaria'],
    zajam: ['ADO zajam', 'ADO loan'],
    garancija: ['Garancija', 'Guarantee'],
    pomocna: ['Pomocna', 'Auxiliary'],
};

const label = (map: Record<string, [string, string]>, key: string, locale: Locale) => (map[key] ? map[key][locale === 'hr' ? 0 : 1] : key);

function Selects({
    role,
    bucket,
    roles,
    buckets,
    locale,
    disabled,
    onRole,
    onBucket,
}: {
    role: string;
    bucket: string;
    roles: string[];
    buckets: string[];
    locale: Locale;
    disabled: boolean;
    onRole: (v: string) => void;
    onBucket: (v: string) => void;
}) {
    return (
        <>
            <td>
                <select value={role} onChange={(e) => onRole(e.target.value)} disabled={disabled} style={{ maxWidth: '220px' }}>
                    {roles.map((r) => (
                        <option key={r} value={r}>
                            {label(ROLE_LABEL, r, locale)}
                        </option>
                    ))}
                </select>
            </td>
            <td>
                <select value={bucket} onChange={(e) => onBucket(e.target.value)} disabled={disabled} style={{ maxWidth: '200px' }}>
                    {['', ...buckets].map((b) => (
                        <option key={b} value={b}>
                            {label(BUCKET_LABEL, b, locale)}
                        </option>
                    ))}
                </select>
            </td>
        </>
    );
}

/** Popup za skladista iz snimke zalihe koja jos nisu razvrstana. */
export function UnknownWarehousesPopup({
    token,
    locale,
    unknown,
    canEdit,
    onDone,
}: {
    token: string;
    locale: Locale;
    unknown: Unknown[];
    canEdit: boolean;
    onDone: () => void;
}) {
    const hr = locale === 'hr';
    const [open, setOpen] = useState(unknown.length > 0);
    const [meta, setMeta] = useState<Payload | null>(null);
    const [draft, setDraft] = useState<Record<string, { role: string; bucket: string }>>({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setOpen(unknown.length > 0);
        setDraft(Object.fromEntries(unknown.map((u) => [u.code, { role: 'poslovnica', bucket: '' }])));
    }, [unknown]);

    useEffect(() => {
        if (!open || meta) return;
        api<Payload>('/gold-coverage-v2/warehouses', token)
            .then(setMeta)
            .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    }, [open, meta, token]);

    if (!open || unknown.length === 0) return null;

    const save = async () => {
        setBusy(true);
        setError(null);
        try {
            for (const u of unknown) {
                const d = draft[u.code] ?? { role: 'poslovnica', bucket: '' };
                await mutate('/gold-coverage-v2/warehouses', token, 'PUT', {
                    code: u.code,
                    name: u.name,
                    coverage_role: d.role,
                    info_bucket: d.bucket === '' ? null : d.bucket,
                });
            }
            setOpen(false);
            onDone();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="card" style={{ background: '#fff', padding: '1.25rem', maxWidth: '960px', width: '96%', maxHeight: '85vh', overflowY: 'auto' }}>
                <h3 style={{ marginTop: 0 }}>{hr ? 'Novo skladiste u Faros snimci' : 'New warehouse in the Faros snapshot'}</h3>
                <p className="muted">
                    {hr
                        ? 'Dok se ne razvrsta, skladiste se racuna kao poslovnica. Odaberi ulogu u modulu 40 i odjeljak pokrivenosti. Kasnije se mijenja u Postavkama, tablica Skladista.'
                        : 'Until classified, the warehouse counts as a branch. Choose its role in module 40 and its coverage section. It can be changed later in Settings, Warehouses table.'}
                </p>
                {error ? <p className="error">{error}</p> : null}
                <table className="data-table" style={{ width: '100%', tableLayout: 'auto' }}>
                    <thead>
                        <tr>
                            <th>{hr ? 'Skladiste' : 'Warehouse'}</th>
                            <th className="num">{hr ? 'Redaka' : 'Rows'}</th>
                            <th>{hr ? 'Uloga' : 'Role'}</th>
                            <th>{hr ? 'Odjeljak' : 'Section'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {unknown.map((u) => {
                            const d = draft[u.code] ?? { role: 'poslovnica', bucket: '' };
                            return (
                                <tr key={u.code}>
                                    <td>
                                        {u.code} {u.name}
                                    </td>
                                    <td className="num">{u.rows}</td>
                                    <Selects
                                        role={d.role}
                                        bucket={d.bucket}
                                        roles={meta?.roles ?? Object.keys(ROLE_LABEL)}
                                        buckets={meta?.buckets ?? Object.keys(BUCKET_LABEL).filter((b) => b !== '')}
                                        locale={locale}
                                        disabled={busy || !canEdit}
                                        onRole={(v) => setDraft((p) => ({ ...p, [u.code]: { ...d, role: v } }))}
                                        onBucket={(v) => setDraft((p) => ({ ...p, [u.code]: { ...d, bucket: v } }))}
                                    />
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {!canEdit ? (
                    <p className="muted">{hr ? 'Nemas ovlast za razvrstavanje; javi uredniku modula 40.61.' : 'You may not classify warehouses; ask a 40.61 editor.'}</p>
                ) : null}
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)} disabled={busy}>
                        {hr ? 'Kasnije' : 'Later'}
                    </button>
                    {canEdit ? (
                        <button type="button" className="btn" onClick={() => void save()} disabled={busy}>
                            {hr ? 'Spremi razvrstavanje' : 'Save classification'}
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

/** Postavke: uloga i odjeljak za svako skladiste, mijenja se po skladistu. */
export function WarehouseClassifySettings({ token, locale, onChanged }: { token: string; locale: Locale; onChanged: () => void }) {
    const hr = locale === 'hr';
    const [data, setData] = useState<Payload | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            setData(await api<Payload>('/gold-coverage-v2/warehouses', token));
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    }, [token]);

    useEffect(() => {
        if (open && !data) void load();
    }, [open, data, load]);

    const change = async (w: Wh, role: string, bucket: string) => {
        setBusy(w.code);
        setError(null);
        try {
            await mutate('/gold-coverage-v2/warehouses', token, 'PUT', {
                code: w.code,
                coverage_role: role,
                info_bucket: bucket === '' ? null : bucket,
            });
            await load();
            onChanged();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(null);
        }
    };

    return (
        <div style={{ marginTop: '1rem' }}>
            <button type="button" className="link-btn" onClick={() => setOpen((v) => !v)}>
                {open ? (hr ? 'Sakrij skladista' : 'Hide warehouses') : hr ? 'Skladista: uloga i odjeljak' : 'Warehouses: role and section'}
            </button>
            {open ? (
                <>
                    {error ? <p className="error">{error}</p> : null}
                    {!data ? (
                        <p className="muted">{hr ? 'Ucitavam...' : 'Loading...'}</p>
                    ) : (
                        <table className="data-table" style={{ marginTop: '0.5rem' }}>
                            <thead>
                                <tr>
                                    <th>{hr ? 'Skladiste' : 'Warehouse'}</th>
                                    <th>{hr ? 'Vlasnistvo' : 'Ownership'}</th>
                                    <th>{hr ? 'Uloga' : 'Role'}</th>
                                    <th>{hr ? 'Odjeljak' : 'Section'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.warehouses.map((w) => (
                                    <tr key={w.code}>
                                        <td>
                                            {w.code} {w.name}
                                        </td>
                                        <td>{w.ownership_type ?? '-'}</td>
                                        <Selects
                                            role={w.coverage_role}
                                            bucket={w.info_bucket ?? ''}
                                            roles={data.roles}
                                            buckets={data.buckets}
                                            locale={locale}
                                            disabled={busy !== null || !data.can_edit}
                                            onRole={(v) => void change(w, v, w.info_bucket ?? '')}
                                            onBucket={(v) => void change(w, w.coverage_role, v)}
                                        />
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            ) : null}
        </div>
    );
}
